import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  Book as BookIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Add as AddIcon,
  TrendingUp as TrendingIcon,
  FileDownload as FileDownloadIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  ShowChart as ShowChartIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { statsAPI, livresAPI, tagsAPI } from '../services/api';
import toast from 'react-hot-toast';
import BooksPerStatus from '../components/BooksPerStatus';
import BooksPerLibrary from '../components/BooksPerLibrary';
import TopAuthorsChart from '../components/TopAuthorsChart';
import PopularTagsChart from '../components/PopularTagsChart';


function HomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: stats, isLoading } = useQuery(
    'stats',
    () => statsAPI.getGeneral().then(res => res.data.data),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  // Requête pour récupérer les tags avec leurs statistiques d'utilisation
  const { data: tagsStats, isLoading: isTagsLoading, error: tagsError } = useQuery(
    'tags-stats',
    async () => {
      console.log('🔍 Récupération des statistiques des tags...');
      try {
        const tagsResponse = await tagsAPI.getAll();
        const tags = tagsResponse.data.data;
        const totalTags = tagsResponse.data.pagination?.total || tags.length;
        console.log('📊 Tags récupérés:', tags.length, 'Total:', totalTags);
        
        if (tags.length === 0) {
          console.log('⚠️ Aucun tag trouvé');
          return { total: 0, topTags: [] };
        }
        
        // Pour chaque tag, compter le nombre de livres associés
        const tagsWithCount = await Promise.all(
          tags.map(async (tag) => {
            try {
              const livresResponse = await livresAPI.getAll({ 
                tag_id: tag.id, 
                limit: 1 // On ne veut que le count, pas les données
              });
              const count = livresResponse.data.pagination?.total || 0;
              console.log(`📈 Tag "${tag.nom}": ${count} livres`);
              return {
                ...tag,
                nombre_livres: count
              };
            } catch (error) {
              console.warn(`Erreur lors du comptage pour le tag ${tag.nom}:`, error);
              return {
                ...tag,
                nombre_livres: 0
              };
            }
          })
        );
        
        // Trier par nombre de livres décroissant et prendre les 10 premiers
        const topTags = tagsWithCount
          .filter(tag => tag.nombre_livres > 0)
          .sort((a, b) => b.nombre_livres - a.nombre_livres)
          .slice(0, 10);
        
        console.log('🏆 Top tags:', topTags);
        return { total: totalTags, topTags };
      } catch (error) {
        console.error('❌ Erreur lors de la récupération des statistiques des tags:', error);
        return { total: 0, topTags: [] };
      }
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1, // Une seule tentative en cas d'erreur
    }
  );

  const handleExport = async () => {
    try {
      console.log('📊 Début de l\'export Excel depuis la page d\'accueil...');
      const response = await livresAPI.export();
      
      // Créer un blob et télécharger le fichier
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Nom du fichier avec la date
      const today = new Date().toISOString().split('T')[0];
      link.download = `livres_export_${today}.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('homePage.exportSuccess'));
      console.log('✅ Export Excel terminé');
      
    } catch (error) {
      console.error('❌ Erreur export Excel:', error);
      toast.error(t('homePage.exportError'));
    }
  };

  // Calculer les statistiques de lecture
  const totalLivres = stats?.totalLivres || 0;
  const livresLus = stats?.statutsLecture?.lu || 0;
  const livresEnCours = stats?.statutsLecture?.en_cours || 0;
  const livresAbandonnes = stats?.statutsLecture?.abandonne || 0;
  //const livresNonLus = stats?.statutsLecture?.non_lu || 0;
  
  const pourcentageLus = totalLivres > 0 ? Math.round((livresLus / totalLivres) * 100) : 0;
  const pourcentageEnCours = totalLivres > 0 ? Math.round((livresEnCours / totalLivres) * 100) : 0;
  const pourcentageAbandonnes = totalLivres > 0 ? Math.round((livresAbandonnes / totalLivres) * 100) : 0;
  
  // Calculer les statistiques pour les nouveaux blocs
  const progressionLecture = pourcentageLus;
  const nombreBibliotheques = stats?.totalBibliotheques || 0;
  const nombreEtiquettesUtilisees = tagsStats?.total || 0;
  
  // Statistiques pour les 3 nouveaux blocs
  const nombreAuteursUniques = stats?.totalAuteurs || 0;
  const nombreEtageresUniques = stats?.totalRayonnages || 0;
  const nombreEditeursUniques = stats?.totalEditeurs || 0;


  return (
    <Box>
      {/* Bandeau de couleur avec titre et bouton */}
      <Box
        sx={{
          background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
          borderRadius: 2,
          p: 4,
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <ShowChartIcon sx={{ fontSize: 28, color: 'white', mr: 1 }} />
            <Typography variant="h4" component="h1" sx={{ color: 'white', fontWeight: 700 }}>
              {t('homePage.title')}
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9 }}>
            {t('homePage.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<BookIcon />}
          onClick={() => navigate('/livres')}
          sx={{
            backgroundColor: 'white',
            color: '#9c27b0',
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
            },
            '& .MuiSvgIcon-root': {
              color: '#9c27b0',
            },
          }}
        >
          {t('homePage.viewBooks')}
        </Button>
      </Box>

      {/* Statistiques de lecture */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {/* Total */}
        <Grid item sm={3} xs={6}>
          <Card 
            sx={{ 
              borderRadius: 2, 
              boxShadow: 2,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px) scale(1.02)',
              }
            }}
            onClick={() => navigate('/livres')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BookIcon sx={{ fontSize: 24, color: 'text.secondary', mr: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {t('homePage.total')}
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                {isLoading ? <CircularProgress size={24} /> : totalLivres}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('homePage.books')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Lus */}
        <Grid item sm={3} xs={6}>
          <Card 
            sx={{ 
              borderRadius: 2, 
              boxShadow: 2,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px) scale(1.02)',
              }
            }}
            onClick={() => navigate('/livres?statut=lu')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1,
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 16, color: 'white' }} />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                  {t('homePage.read')}
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'success.main', mb: 0.5 }}>
                {isLoading ? <CircularProgress size={24} /> : livresLus}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {pourcentageLus}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* En cours */}
        <Grid item sm={3} xs={6}>
          <Card 
            sx={{ 
              borderRadius: 2, 
              boxShadow: 2,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px) scale(1.02)',
              }
            }}
            onClick={() => navigate('/livres?statut=en_cours')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1,
                    flexShrink: 0,
                  }}
                >
                  <ScheduleIcon sx={{ fontSize: 16, color: 'white' }} />
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500, 
                    color: 'primary.main',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('homePage.inProgress')}
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
                {isLoading ? <CircularProgress size={24} /> : livresEnCours}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {pourcentageEnCours}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Abandonnés */}
        <Grid item sm={3} xs={6}>
          <Card 
            sx={{ 
              borderRadius: 2, 
              boxShadow: 2,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px) scale(1.02)',
              }
            }}
            onClick={() => navigate('/livres?statut=abandonne')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1,
                    flexShrink: 0,
                  }}
                >
                  <CancelIcon sx={{ fontSize: 16, color: 'white' }} />
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500, 
                    color: 'text.secondary',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('homePage.abandoned')}
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                {isLoading ? <CircularProgress size={24} /> : livresAbandonnes}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {pourcentageAbandonnes}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Nouveaux blocs statistiques */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {/* Progression de lecture */}
        <Grid item xs={12} sm={4}>
          <Card sx={{ 
            borderRadius: 2, 
            boxShadow: 2,
            background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
            color: 'white',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 2, 
                  opacity: 0.9,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {t('homePage.readingProgress')}
              </Typography>
              <Typography variant="h3" component="div" sx={{ fontWeight: 600, mb: 2, flex: 1 }}>
                {isLoading ? <CircularProgress size={32} sx={{ color: 'white' }} /> : `${progressionLecture}%`}
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  height: 8,
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  borderRadius: 4,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${progressionLecture}%`,
                    height: '100%',
                    backgroundColor: 'white',
                    transition: 'width 0.3s ease-in-out',
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Nombre de bibliothèques */}
        <Grid item xs={12} sm={4}>
          <Card 
            onClick={() => navigate('/bibliotheques')}
            sx={{ 
              borderRadius: 2, 
              boxShadow: 2,
              background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
              color: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px) scale(1.02)',
              }
            }}
          >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 2, 
                  opacity: 0.9,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {t('homePage.libraries')}
              </Typography>
              <Typography variant="h3" component="div" sx={{ fontWeight: 600, mb: 1, flex: 1 }}>
                {isLoading ? <CircularProgress size={32} sx={{ color: 'white' }} /> : nombreBibliotheques}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  opacity: 0.9,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {t('homePage.differentLocations')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Étagères */}
        <Grid item xs={12} sm={4}>
          <Card sx={{ 
            borderRadius: 2, 
            boxShadow: 2,
            background: 'linear-gradient(135deg, #e91e63 0%, #c2185b 100%)',
            color: 'white',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 2, 
                  opacity: 0.9,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {t('homePage.shelves')}
              </Typography>
              <Typography variant="h3" component="div" sx={{ fontWeight: 600, mb: 1, flex: 1 }}>
                {isLoading ? <CircularProgress size={32} sx={{ color: 'white' }} /> : nombreEtageresUniques}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  opacity: 0.9,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {t('homePage.differentLocations')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Nouveaux blocs : Auteurs, Étagères, Éditeurs uniques */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {/* Tags utilisées */}
        <Grid item xs={12} sm={4}>
          <Card 
            onClick={() => navigate('/tags')}
            sx={{ 
              borderRadius: 2, 
              boxShadow: 2,
              backgroundColor: '#4caf50',
              color: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px) scale(1.02)',
              }
            }}
          >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 2, 
                  opacity: 0.9,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {t('homePage.tagsUsed')}
              </Typography>
              <Typography variant="h3" component="div" sx={{ fontWeight: 600, mb: 1, flex: 1 }}>
                {isLoading ? <CircularProgress size={32} sx={{ color: 'white' }} /> : nombreEtiquettesUtilisees}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  opacity: 0.9,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {t('homePage.activeCategories')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Auteurs */}
        <Grid item xs={12} sm={4}>
          <Card 
            onClick={() => navigate('/auteurs')}
            sx={{ 
              borderRadius: 2, 
              boxShadow: 2,
              backgroundColor: '#ff9800',
              color: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px) scale(1.02)',
              }
            }}
          >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 2, 
                  opacity: 0.9,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {t('homePage.authors')}
              </Typography>
              <Typography variant="h3" component="div" sx={{ fontWeight: 600, mb: 1, flex: 1 }}>
                {isTagsLoading ? <CircularProgress size={32} sx={{ color: 'white' }} /> : nombreAuteursUniques}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  opacity: 0.9,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {t('homePage.differentAuthors')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Éditeurs uniques */}
        <Grid item xs={12} sm={4}>
          <Card 
            onClick={() => navigate('/editeurs')}
            sx={{ 
              borderRadius: 2, 
              boxShadow: 2,
              backgroundColor: '#f44336',
              color: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px) scale(1.02)',
              }
            }}
          >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 2, 
                  opacity: 0.9,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {t('homePage.publishers')}
              </Typography>
              <Typography variant="h3" component="div" sx={{ fontWeight: 600, mb: 1, flex: 1 }}>
                {isLoading ? <CircularProgress size={32} sx={{ color: 'white' }} /> : nombreEditeursUniques}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  opacity: 0.9,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {t('homePage.differentPublishers')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Graphique en camembert - Répartition par statut */}
      <BooksPerStatus stats={stats} isLoading={isLoading} />

      {/* Graphique en camembert - Livres par bibliothèque */}
      <BooksPerLibrary stats={stats} isLoading={isLoading} />

      {/* Top auteurs - Graphique en barres horizontales */}
      <TopAuthorsChart stats={stats} isLoading={isLoading} />

      {/* Étiquettes populaires - Graphique en barres verticales */}
      <PopularTagsChart tagsStats={tagsStats?.topTags || []} isLoading={isTagsLoading} />

      {/* Bouton d'export en bas du tableau de bord */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          sx={{
            backgroundColor: 'white',
            color: 'text.primary',
            borderColor: 'grey.300',
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 500,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            '&:hover': {
              backgroundColor: 'grey.50',
              borderColor: 'grey.400',
            },
          }}
        >
          {t('homePage.exportData')}
        </Button>
      </Box>

    </Box>
  );
}

export default HomePage;
